<template>
    <div class="doc-viewer-lines">
        <DocumentViewerLinesSelectsBatchUpdate
            v-if="!isSettingsEditMode"
            :isLandedCost="isLandedCost"/>
        <ag-grid-vue
            :key="`ag-grid-${isLandedCost}`"
            :style="`height: ${tableHeight}px;`"
            class="ag-theme-balham"
            @grid-ready="onGridReady"
            @cellValueChanged="onCellValueChanged"
            @cellClicked="onCellClicked"
            @click.navive.stop
            @selectionChanged="onSelectionChanged"
            domLayout="normal"
            :rowHeight="34"
            :rowBuffer="2"
            :getRowNodeId="getRowNodeId"
            :sideBar="sideBar"
            :columnDefs="columnDefs"
            :defaultColDef="defaultColDef"
            :navigateToNextCell="navigateToNextCell"
            :detailCellRenderer="detailCellRenderer"
            :frameworkComponents="frameworkComponents"
            :tooltipShowDelay="1500"
            :debounceVerticalScrollbar="true"
            :applyColumnDefOrder="true"
            :immutableData="true"
            :masterDetail="true"
            :enableFillHandle="true"
            :enableRangeSelection="true"
            :enableCellChangeFlash="!isSettingsEditMode"
            :suppressCellSelection="false"
            :stopEditingWhenGridLosesFocus="true"
            :suppressCopyRowsToClipboard="true"
            :processCellForClipboard="processCellForClipboard"
            :processCellFromClipboard="processCellFromClipboard"
            :getContextMenuItems="getContextMenuItems"
            rowSelection="multiple">
        </ag-grid-vue>
        <div class="table-status-bar d-flex">
            <div class="flex-grow-1 text-left">
                Lines: {{ filteredLines.length }}
            </div>
            <div class="flex-grow-1 text-right">
                <template v-if="isStatements">
                    Balance: {{ displayCurrency }} {{ formattedBalance }}
                </template>
                <template v-else>
                    <template v-if="!isLandedCost && checkIsVisibleFieldByPath('lines.total')">
                        Total:
                        <span>{{ displayCurrency }} {{ formattedTotalSum }}</span>
                    </template>
                    <span v-if="calculateDocTax" class="pl-3">
                        Tax:
                        <span>{{ displayCurrency }} {{ formattedTaxSum }}</span>
                    </span>
                </template>
            </div>
        </div>

        <div v-if="hasLinesSplitAndTotal && incorrectSplitSum" class="text-right m-0 pt-1">
            <small class="text-danger">
                <span v-tooltip="'Split amount must be equal to 100%'">
                    <i class="fal fa-exclamation-circle"></i>
                </span>
                Incorrect split amount ({{ splitSumPercentage }}%)
            </small>
        </div>
        <div v-if="canEditDoc" class="row mx-0 mt-2">
            <div class="col-6 p-0 text-left">
                <template v-if="allowLineCreation">
                    <button @click.prevent="onAddLine({isLandedCost})"
                            class="btn btn-sm border rounded clickable px-3 mr-2">
                        Add line
                    </button>
                </template>
            </div>
            <div class="col-6 text-right pr-0">
                <div v-if="selectedRows.length !== 0" class="d-inline-block border rounded px-2" style="height: 31px">
                    ({{ selectedRows.length }}) Selected {{ 'row' | pluralize(selectedRows.length) }}:
                    <template v-if="formHasLandedCostLines">
                        <button v-if="isLandedCost"
                                @click="changeItemType(AppDocLineTypes.ITEM)"
                                v-tooltip="`Move to items`"
                                class="btn btn-sm text-info ml-1">
                            <i class="fa fa-angle-double-up fa-lg"></i>
                        </button>
                        <button v-else
                                v-tooltip="`Move to landed cost`"
                                @click="changeItemType(AppDocLineTypes.LANDED_COST)"
                                class="btn btn-sm text-info ml-1">
                            <i class="fa fa-angle-double-down fa-lg"></i>
                        </button>
                    </template>

                    <button v-tooltip="'Duplicate selected lines'"
                            @click="onDuplicateLines"
                            class="btn btn-sm text-info ml-1">
                        <i class="fal fa-copy fa-lg"/>
                    </button>

                    <button @click="onRemoveLines"
                            v-tooltip="'Delete selected lines'"
                            class="btn btn-sm text-danger">
                        <i class="fal fa-trash-alt fa-lg"></i>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {cloneDeep, get, isArray, isNumber, isObject, range, set, uniqBy} from 'lodash'
import accounting from 'accounting'
import {mapActions, mapGetters, mapMutations} from 'vuex'
import AgGridCellEditorMultiselect from './ag-grid/editors/AgGridCellEditorMultiselect'
import AgGridCellEditorStockCode from './ag-grid/editors/AgGridCellEditorStockCode'
import AgGridCellEditorTextarea from './ag-grid/editors/AgGridCellEditorTextarea'
import AgGridCellEditorDate from './ag-grid/editors/AgGridCellEditorDate'
import AgGridCellRendererStockCode from './ag-grid/renderers/components/AgGridCellRendererStockCode'
import AgGridCellRendererMatchingItem from './ag-grid/renderers/components/AgGridCellRendererMatchingItem'
import AgGridCellTooltip from './ag-grid/tooltip/AgGridCellTooltip'
import AgGridCellRendererDetails from './ag-grid/renderers/components/AgGridCellRendererDetails'

import {
    DOC_CLEAR_DEPENDENT_FIELD_VALUES,
    DOC_FIELD_RUN_ACTIONS,
    DOC_FIELD_UPDATE,
    DOC_LINE_ADD,
    DOC_LINE_ON_CHANGE_SKU,
    DOC_LINE_REMOVE,
    DOC_LINE_UOM_CALCULATE_BASE_PRICE,
    DOC_LINE_UOM_CALCULATE_CONVERTED_QTY,
    DOC_LINE_UOM_CALCULATE_PRICE,
    DOC_LINE_UOM_SET_PRICE_FROM_ITEM_CONVERSION_TABLE,
    DOC_LINES_BATCH_UPDATE,
    DOC_RECALCULATE_LINE_TAX,
    DOC_RECALCULATE_LINES_SPLIT,
    DOC_SET_LINES
} from '@/store/modules/doc-viewer'
import {forceToNumber} from '@/utils'
import {FieldEditorTypes, getFormFieldFormattedLabel} from '@/types/document-viewer/field/modules/editor'
import {FormSystemSections} from '@/types/document-viewer/section'
import {getFieldPathMeta} from '@/types/document-viewer/field/modules/path'
import {MatchingExtensionProcessLevel} from '@/types/document-viewer/extension/matching'
import DocumentViewerLinesSelectsBatchUpdate from './batch-update/DocumentViewerLinesSelectsBatchUpdate'
import {DOC_PO_ADJUST_LINE_PO} from '@/store/modules/doc-viewer/matching/purchase-order'
import {DOC_RECEIPT_ADJUST_LINE_PO} from '@/store/modules/doc-viewer/matching/supplier-receipt'
import {
    FieldEditorOptionsSources,
    FieldStorageOptionsGetters,
    getFieldStorageOptionsLabel,
    getFieldStorageOptionsTrackBy
} from '@/types/document-viewer/field/modules/editor/select'
import {getFieldValidationLevelMeta, getFieldValidationPriority} from '@/types/document-viewer/field/modules/validation'
import AgGridCellRendererPrice from './ag-grid/renderers/components/AgGridCellRendererPrice'
import AgGridCellRendererSupplierCode from './ag-grid/renderers/components/AgGridCellRendererSupplierCode.vue'
import {FieldRendererAlign} from '@/types/document-viewer/field/modules/renderer'
import {AppDocLineTypes} from '@/types/document/lines'
import {checkLineSelectFieldHasOptions} from '@/views/documents/single/view/lines/index'
import * as clipboard from 'clipboard-polyfill'
import {removeEmptyMatrixColumns} from '@/utils/array'
import {showErrorMessage} from '@/services/snackbar'
import {tryAutofillPricingFormOnLineFieldChange} from '@/views/documents/single/view/lines/calculations'
import {$lang, evaluateTeamLangPlaceholder} from '@/views/settings/team/lang'
import {
    checkIsReadonlyFieldInAccordanceWithRules,
    checkIsVisibleFieldInAccordanceWithRules
} from '@/types/document-viewer/field/modules/rules'
import AgGridCellRendererContractCheck
    from '@/views/documents/single/view/lines/ag-grid/renderers/components/AgGridCellRendererContractCheck.vue'
import {fetchItemById, fetchItemMetrics} from '@/api/document/items'

export default {
    name: 'DocumentViewerLines',
    components: {
        DocumentViewerLinesSelectsBatchUpdate
    },
    props: {
        isLandedCost: {
            type: Boolean,
            default: false
        },
        changeSelection: {
            type: Function,
            required: false,
            default: () => {
            }
        }
    },
    data() {
        return {
            selectedRows: [],
            getRowNodeId: null,
            gridApi: null,
            columnApi: null,
            columnDefs: null,
            sideBar: null,
            defaultColDef: null,
            detailCellRenderer: null,
            frameworkComponents: null,
            isColumnsSetup: false,
            AppDocLineTypes
        }
    },
    computed: {
        ...mapGetters([
            'isDemoTeam',
            'isNexusSandboxTeam'
        ]),

        ...mapGetters('doc_viewer', [
            'doc',
            'docWithNormalizedParts',
            'lines',
            'splitSumPercentage',
            'incorrectSplitSum',
            'isStatements',
            'linesTotalsSum',
            'linesTaxSumForSelectedCalculationFormula',
            'getFormFieldValue',
            'disabledDocument',
            'matchingSupplier',
            'checkContractResponse',
            'isSavingChanges',
            'docUniqueSubsidiaries'
        ]),

        ...mapGetters('doc_viewer_access_level', [
            'canEditDoc'
        ]),

        ...mapGetters('doc_viewer_po', [
            'purchaseOrder'
        ]),

        ...mapGetters('doc_viewer_supplier_receipt', [
            'deliveryDocket'
        ]),

        ...mapGetters('doc_viewer_settings', [
            'settings',
            'sections',
            'fields',
            'visibleFieldsWithStateRulesApplied',
            'checkIsVisibleFieldWithStateRulesApplied',
            'poCheckEnabled',
            'poProcessLevel',
            'supplierReceiptsCheckEnabled',
            'supplierReceiptsProcessLevel',
            'allowLineCreation',
            'calculateLineTotal',
            'calculateLineTax',
            'calculateDocTotal',
            'calculateDocSubtotal',
            'calculateDocTax',
            'calculateDocTaxAndOverride',
            'calculateDocFreight',
            'checkIsVisibleFieldByPath',
            'isSettingsEditMode',
            'getFieldSettingsByPath',
            'getRoundingDecimalsByFieldPath',
            'getThousandSeparatorByFieldPath',
            'hasLinesSplitAndTotal',
            'withLineUomConversion'
        ]),

        ...mapGetters('doc_viewer_metadata', [
            'formMetadata',
            'getFieldMetadata',
            'isTouchedForm'
        ]),

        ...mapGetters('documents', [
            'isDemoContracts'
        ]),

        withPurchaseOrderColumn() {
            return this.poCheckEnabled &&
                this.poProcessLevel === MatchingExtensionProcessLevel.LINES && !this.isLandedCost
        },

        withReceiptsColumn() {
            return this.supplierReceiptsCheckEnabled &&
                this.supplierReceiptsProcessLevel === MatchingExtensionProcessLevel.LINES && !this.isLandedCost
        },

        filteredLines() {
            return this.lines
                .map((l, lineIdx) => ({...l, lineIdx}))
                .filter(l => {
                    if (l.isDeleted) {
                        return false
                    }
                    if (this.isStatements || this.isDemoContracts) {
                        return true
                    }
                    return l.type === (this.isLandedCost ? AppDocLineTypes.LANDED_COST : AppDocLineTypes.ITEM)
                })
        },

        tableHeight() {
            if (this.filteredLines.length <= 5) {
                return 6 * 34
            } else if (this.filteredLines.length >= 8) {
                return 9 * 34
            } else {
                return (this.filteredLines.length + 1) * 34
            }
        },

        extensions() {
            return this.settings.extensions || []
        },

        formHasLandedCostLines() {
            return this.sections.some(s => s.key === FormSystemSections.LANDED_COST_LINES && s.visible)
        },

        displayCurrency() {
            return get(this.doc, 'currency.symbol')
        },

        formattedTotalSum() {
            return accounting.formatMoney(this.linesTotalsSum, {
                symbol: '',
                format: '%v',
                decimal: '.',
                thousand: this.getThousandSeparatorByFieldPath('lines.total'),
                precision: this.getRoundingDecimalsByFieldPath('lines.total')
            })
        },

        formattedTaxSum() {
            return accounting.formatMoney(this.linesTaxSumForSelectedCalculationFormula, {
                symbol: '',
                format: '%v',
                decimal: '.',
                thousand: this.checkIsVisibleFieldByPath('lines.tax')
                    ? this.getThousandSeparatorByFieldPath('lines.tax')
                    : this.getThousandSeparatorByFieldPath('lines.total'),
                precision: this.checkIsVisibleFieldByPath('lines.tax')
                    ? this.getRoundingDecimalsByFieldPath('lines.tax')
                    : this.getRoundingDecimalsByFieldPath('lines.total')
            })
        },

        formattedBalance() {
            const balance = this.lines.reduce((acc, line) => acc + line.balance, 0)
            return accounting.formatMoney(balance, {
                symbol: '',
                format: '%v',
                decimal: '.',
                thousand: this.getThousandSeparatorByFieldPath('lines.balance'),
                precision: this.getRoundingDecimalsByFieldPath('lines.balance')
            })
        },

        filteredFields() {
            return uniqBy(
                this.fields.filter(f => f.visible && f.section.key === (this.isLandedCost ? FormSystemSections.LANDED_COST_LINES : FormSystemSections.LINES)),
                'path'
            ).sort((a, b) => a.section.order - b.section.order)
        },

        lineFieldsMetadata() {
            return this.formMetadata.filter(f => this.isLandedCost ? f.path.startsWith('lcLines.') : f.path.startsWith('lines.'))
        },

        hasSupplierCodeColumn() {
            return this.filteredFields.some(f => f.path === 'lines.supplierCode')
        },

        hasOnOrderMetricColumn() {
            return this.filteredFields.some(f => f.path === 'lines.onOrder')
        },

        firstSubsidiaryId() {
            return _.get(_.first(this.docUniqueSubsidiaries), 'uuid')
        }
    },
    methods: {
        ...mapActions('doc_viewer', {
            updateField: DOC_FIELD_UPDATE,
            updateLinesData: DOC_LINES_BATCH_UPDATE,
            removeLine: DOC_LINE_REMOVE,
            addLine: DOC_LINE_ADD,
            onDocLineSkuChange: DOC_LINE_ON_CHANGE_SKU,
            runFieldActions: DOC_FIELD_RUN_ACTIONS,
            recalculateLineTax: DOC_RECALCULATE_LINE_TAX,
            recalculateLinesSplit: DOC_RECALCULATE_LINES_SPLIT,
            clearDependentFieldValues: DOC_CLEAR_DEPENDENT_FIELD_VALUES,

            calculateUomExtensionPrice: DOC_LINE_UOM_CALCULATE_PRICE,
            calculateUomExtensionBasePrice: DOC_LINE_UOM_CALCULATE_BASE_PRICE,
            calculateUomExtensionConvertedQty: DOC_LINE_UOM_CALCULATE_CONVERTED_QTY,
            setUomExtensionPriceFromItemConversionTable: DOC_LINE_UOM_SET_PRICE_FROM_ITEM_CONVERSION_TABLE
        }),

        ...mapActions('doc_viewer_po', {
            adjustLinePo: DOC_PO_ADJUST_LINE_PO
        }),

        ...mapActions('doc_viewer_supplier_receipt', {
            adjustLineReceipt: DOC_RECEIPT_ADJUST_LINE_PO
        }),

        ...mapMutations('doc_viewer', {
            updateLines: DOC_SET_LINES
        }),

        async onGridReady(params) {
            this.gridApi = params.api
            this.columnApi = params.columnApi

            this.gridApi.showLoadingOverlay()
            await this.adjustCellValuesBeforeFirstRender()
            await this.setupColumns()
            this.gridApi.setRowData(this.filteredLines)
            this.gridApi.setFillHandleDirection('y')
            this.gridApi.hideOverlay()
        },

        onCellClicked(event) {
            if (!this.isSettingsEditMode) {
                return
            }
            let {colDef} = event
            const prop = colDef.fieldName
            const fieldSettingsSection = this.isLandedCost ? 'lcLines' : 'lines'
            const fieldSettingsPath = colDef.isCustom
                ? `${fieldSettingsSection}.custom.${prop}`
                : `${fieldSettingsSection}.${prop}`

            this.changeSelection({
                section: this.isLandedCost ? FormSystemSections.LANDED_COST_LINES : FormSystemSections.LINES,
                field: fieldSettingsPath
            })
        },

        async onCellValueChanged(event) {
            let {value, colDef, data: {lineIdx}} = event
            const prop = colDef.fieldName
            const path = colDef.isCustom ? `lines.custom.${prop}` : `lines.${prop}`
            if (prop === 'split') {
                value = forceToNumber(value)
                if (value > 100) {
                    value = 100
                } else if (value < 0) {
                    value = 0
                }
            }
            this.updateField({path, idx: lineIdx, data: value})
            this.clearDependentFieldValues({path, idx: lineIdx})

            if (prop === 'sku') {
                await this.onDocLineSkuChange({sku: value, idx: lineIdx})
                await this.setSupplierCodeIfRequired(lineIdx)
                await this.checkItemOnOrderMetric(lineIdx)
            } else if (prop === 'supplier') {
                await this.setSupplierCodeIfRequired(lineIdx)
            } else if (prop === 'qty') {
                await this.onQtyChange({lineIdx})
            } else if (prop === 'price') {
                await this.onPriceChange({lineIdx})
            } else if (prop === 'unit_of_measure') {
                await this.onUnitOfMeasureChange(lineIdx)
            }

            if (['qty', 'price', 'discount', 'tax', 'total', 'split',
                 'balance', 'debit', 'credit', 'freight', 'taxType'].includes(prop)
            ) {
                await tryAutofillPricingFormOnLineFieldChange({lineIdx, emittedByProp: prop})
            }
            this.runFieldActions({path, idx: lineIdx})
        },

        navigateToNextCell(params) {
            const suggestedNextCell = params.nextCellPosition

            const KEY_UP = 38
            const KEY_DOWN = 40

            const noUpOrDownKeyPressed = params.key !== KEY_DOWN && params.key !== KEY_UP
            if (noUpOrDownKeyPressed) {
                return suggestedNextCell
            }

            this.gridApi.forEachNode(node => {
                if (node && suggestedNextCell && node.rowIndex === suggestedNextCell.rowIndex) {
                    node.setSelected(true)
                }
            })

            return suggestedNextCell
        },

        processCellForClipboard(params) {
            if (isObject(params.value)) {
                return JSON.stringify(params.value)
            }
            return params.value
        },

        processCellFromClipboard(params) {
            try {
                return cloneDeep(JSON.parse(params.value))
            } catch (e) {
                return params.value
            }
        },

        getContextMenuItems() {
            return [{
                icon: '<i class="fal fa-file-excel text-success"></i>',
                name: 'Excel Export',
                action: () => {
                    this.gridApi.exportDataAsExcel({
                        processCellCallback: cell => {
                            let value = cell.value
                            const editor = get(cell, 'column.colDef.cellRendererParams.settings.editor', {})
                            if ([FieldEditorTypes.DD_SINGLE, FieldEditorTypes.DD_MULTIPLE].includes(editor.type)) {
                                value = getFormFieldFormattedLabel({editor, value})
                            }
                            return value
                        }
                    })
                }
            }, 'copy', {
                icon: '<i class="fal fa-copy"></i>',
                name: 'Copy as Plain Text',
                action: () => {
                    try {
                        const valuesMatrix = [[]]
                        const ranges = this.gridApi.getCellRanges()
                        ranges.forEach(r => {
                            const rowIndexes = range(r.startRow.rowIndex, r.endRow.rowIndex + 1)
                            rowIndexes.forEach(rowIdx => {
                                r.columns.forEach(col => {
                                    const colDef = col.getColDef()
                                    const editor = get(colDef, 'cellRendererParams.settings.editor', {})
                                    let value = get(this.filteredLines.find(l => l.lineIdx === rowIdx), colDef.fieldName)
                                    if ([FieldEditorTypes.DD_SINGLE, FieldEditorTypes.DD_MULTIPLE].includes(editor.type)) {
                                        value = getFormFieldFormattedLabel({editor, value})
                                    }
                                    if (!isArray(get(valuesMatrix, rowIdx))) {
                                        set(valuesMatrix, rowIdx, [])
                                    }
                                    set(valuesMatrix, [rowIdx, Number(col.colId)], value)
                                })
                            })
                        })
                        const clipboardValue = removeEmptyMatrixColumns(valuesMatrix, v => v !== null && v !== undefined)
                            .map(row => row.join('\t'))
                            .join('\n')
                        clipboard.writeText(clipboardValue)
                    } catch (e) {
                        showErrorMessage('Unable to copy to clipboard')
                        this.$raven.captureException(e)
                    }
                }
            }]
        },

        onSelectionChanged() {
            this.selectedRows = this.gridApi.getSelectedRows()
        },

        async changeItemType(type = AppDocLineTypes.LANDED_COST) {
            for (const {lineIdx} of this.selectedRows) {
                await this.updateField({path: 'lines.type', idx: lineIdx, data: type})
                await tryAutofillPricingFormOnLineFieldChange({lineIdx, emittedByProp: 'total'})
            }
        },

        async onAddLine({isLandedCost}) {
            this.addLine({isLandedCost})
            await this.$nextTick()
            this.gridApi.ensureIndexVisible(this.filteredLines.length - 1, 'bottom')
            this.setupLineDefaultValues(this.lines.length - 1)
        },

        async onRemoveLines() {
            for (const {lineIdx} of this.selectedRows) {
                await this.removeLine({idx: lineIdx})
                await tryAutofillPricingFormOnLineFieldChange({lineIdx, emittedByProp: 'total'})
            }
        },

        async onDuplicateLines() {
            for (const {lineIdx} of this.selectedRows) {
                await this.addLine({isLandedCost: this.isLandedCost, duplicateFromLineIdx: lineIdx})
                await tryAutofillPricingFormOnLineFieldChange({lineIdx: this.lines.length - 1, emittedByProp: 'total'})
            }
        },

        async onQtyChange({lineIdx}) {
            this.adjustLinePo({docLineIdx: lineIdx})
            this.adjustLineReceipt({docLineIdx: lineIdx})
            this.tryToCalculateConvertedQty(lineIdx)
        },

        async tryToCalculateConvertedQty(lineIdx) {
            if (!this.withLineUomConversion) {
                return
            }
            const itemUomConversions = await this.getItemUomConversions(lineIdx)
            return this.calculateUomExtensionConvertedQty({idx: lineIdx, item: {itemUomConversions}})
        },

        tryToCalculateConvertedQtyForAllLines() {
            if (!this.withLineUomConversion) {
                return
            }
            this.lines.forEach((line, lineIdx) => {
                this.tryToCalculateConvertedQty(lineIdx)
            })
        },

        async getItemUomConversions(lineIdx) {
            const line = this.lines[lineIdx]
            const itemId = _.get(line, 'item_uuid')
            if (!itemId) {
                return []
            }
            const lineHasValidUomConversionsAttached =
                isArray(line.itemUomConversions) &&
                line.itemUomConversions.every(c => get(c, 'item.uuid') === line.item_uuid)

            if (lineHasValidUomConversionsAttached) {
                return line.itemUomConversions
            } else {
                const item = await fetchItemById(itemId)
                return get(item, 'itemUomConversions') || []
            }
        },

        async onPriceChange({lineIdx}) {
            if (this.withLineUomConversion) {
                const itemId = _.get(this.lines[lineIdx], 'item_uuid')
                const item = await fetchItemById(itemId)
                return this.calculateUomExtensionBasePrice({idx: lineIdx, item})
            }
        },

        async onUnitOfMeasureChange(lineIdx) {
            if (!this.withLineUomConversion) {
                return
            }
            const line = this.lines[lineIdx] || {}
            const item = await fetchItemById(line.item_uuid)

            await this.setUomExtensionPriceFromItemConversionTable({idx: lineIdx, item})
            await this.calculateUomExtensionBasePrice({idx: lineIdx, item})
            await this.calculateUomExtensionConvertedQty({idx: lineIdx, item})
        },

        async getItemOnOrderMetric(itemId) {
            if (!itemId) {
                return null
            }
            const data = await fetchItemMetrics({
                subsidiaryId: this.firstSubsidiaryId,
                itemIds: [itemId]
            })
            const metric = data.find(m => m.item === itemId)
            if (!metric) {
                return null
            }
            return metric.onOrder
        },

        async checkItemOnOrderMetric(lineIdx) {
            if (!this.hasOnOrderMetricColumn) {
                return
            }
            const line = this.lines[lineIdx] || {}
            const itemId = line.item_uuid
            const onOrder = await this.getItemOnOrderMetric(itemId)
            await this.updateField({path: 'lines.onOrder', idx: lineIdx, data: onOrder})
        },

        async checkItemOnOrderMetricForAllLines() {
            if (!this.hasOnOrderMetricColumn) {
                return
            }
            const itemIds = this.lines.map(l => l.item_uuid).filter(Boolean)
            const data = await fetchItemMetrics({
                subsidiaryId: this.firstSubsidiaryId,
                itemIds: itemIds
            })
            const linesBatchUpdateData = []
            this.lines.forEach((line, lineIdx) => {
                const metric = data.find(m => m.item === line.item_uuid)
                linesBatchUpdateData.push({
                    path: 'onOrder',
                    idx: lineIdx,
                    data: metric ? metric.onOrder : null
                })
            })
            await this.updateLinesData(linesBatchUpdateData)
        },

        async getSupplierCodeToSet(lineIdx) {
            const line = this.lines[lineIdx] || {}
            const isVisibleLineSupplier = this.checkIsVisibleFieldByPath('lines.supplier')
            const lineSupplier = get(line, 'supplier.uuid')
            const rootSupplier = get(this.matchingSupplier, 'uuid')
            const supplierId = isVisibleLineSupplier && lineSupplier ? lineSupplier : rootSupplier
            if (!supplierId) {
                return null
            }
            const item = await fetchItemById(line.item_uuid)
            if (!item) {
                return null
            }
            const supplierAlternatives = item.alternatives.filter(a => get(a, 'supplier.uuid') === supplierId)
            if (supplierAlternatives.length === 0) {
                return null
            }
            const preferredAlternative = supplierAlternatives.find(a => a.status === 'PREFERRED')
            const alternative = preferredAlternative || supplierAlternatives[0]
            if (!alternative) {
                return null
            }
            return {
                code: alternative.externalCode,
                status: alternative.status
            }
        },

        async setSupplierCodeIfRequired(lineIdx) {
            if (!this.hasSupplierCodeColumn) {
                return
            }
            const supplierCode = await this.getSupplierCodeToSet(lineIdx)
            await this.updateField({
                path: 'lines.supplierCode',
                idx: lineIdx,
                data: supplierCode
            })
        },

        getContractCheckColumnDef() {
            return {
                headerName: 'Contract',
                fieldName: 'contract',
                editable: false,
                sortable: false,
                filter: false,
                headerTooltip: 'Contract',
                width: 140,
                cellRendererFramework: AgGridCellRendererContractCheck
            }
        },

        getPoColDef() {
            return {
                headerName: 'PO',
                fieldName: 'po',
                editable: false,
                sortable: false,
                filter: false,
                headerTooltip: 'Purchase order',
                width: 140,
                cellRendererFramework: AgGridCellRendererMatchingItem,
                cellRendererParams: {
                    expandRow: this.expandRow
                }
            }
        },

        getReceiptsColDef() {
            return {
                headerName: 'Receipt',
                fieldName: 'receipt',
                editable: false,
                sortable: false,
                filter: false,
                headerTooltip: 'Receipt',
                width: 140,
                cellRendererFramework: AgGridCellRendererMatchingItem,
                cellRendererParams: {
                    expandRow: this.expandRow
                }
            }
        },

        getLineNumberColumnDef() {
            return {
                headerName: '#',
                headerTooltip: 'Line number',
                field: 'lineIdx',
                width: 60,
                resizable: false,
                filter: false,
                editable: false,
                pinned: 'left',
                cellClass: 'cell-align-center text-muted',
                cellRenderer: this.withPurchaseOrderColumn || this.withReceiptsColumn ? 'agGroupCellRenderer' : null,
                cellRendererFramework: null,
                valueFormatter: ({value}) => value + 1
            }
        },

        /**
         * @return (ColDef | ColGroupDef)[]
         * - ag-grid column that are not in the form settings
         * and that are required for UOM conversion extension.
         * (field described in the form settings has higher priority
         * than these returned default configs).
         */
        getUomConversionMissingColumns() {
            const uomEditor = {
                type: FieldEditorTypes.DD_SINGLE,
                select: {
                    source: FieldEditorOptionsSources.STORAGE,
                    trackBy: getFieldStorageOptionsTrackBy(FieldStorageOptionsGetters.UNIT_OF_MEASURES),
                    label: getFieldStorageOptionsLabel(FieldStorageOptionsGetters.UNIT_OF_MEASURES),
                    storage: {
                        getter: FieldStorageOptionsGetters.UNIT_OF_MEASURES
                    }
                }
            }

            const columns = []
            if (!this.getFieldSettingsByPath('lines.unit_of_measure')) {
                columns.push({
                    headerName: $lang.unitOfMeasure.singular,
                    fieldName: 'unit_of_measure',
                    width: 140,
                    cellEditorFramework: AgGridCellEditorMultiselect,
                    cellEditorParams: {
                        settings: {
                            editor: uomEditor,
                            placeholder: 'Select a value'
                        },
                        getResourceState: () => this.docWithNormalizedParts
                    },
                    valueFormatter: p => getFormFieldFormattedLabel({
                        editor: uomEditor,
                        value: p.data.unit_of_measure
                    }),
                    valueGetter: p => p.data.unit_of_measure,
                    valueSetter: p => {
                        if (p.data.unit_of_measure !== p.newValue) {
                            p.data.unit_of_measure = p.newValue
                            return true
                        }
                    }
                })
            }
            if (!this.isNexusSandboxTeam && !this.getFieldSettingsByPath('lines.uom_conversion')) {
                columns.push({
                    headerName: `${$lang.item.singular} ${$lang.unitOfMeasure.singular}`,
                    fieldName: 'uom_conversion',
                    width: 140,
                    cellEditorFramework: AgGridCellEditorMultiselect,
                    cellEditorParams: {
                        settings: {
                            editor: uomEditor,
                            placeholder: 'Select a value'
                        },
                        getResourceState: () => this.docWithNormalizedParts
                    },
                    valueFormatter: p => getFormFieldFormattedLabel({
                        editor: uomEditor,
                        value: p.data.uom_conversion
                    }),
                    valueGetter: p => p.data.uom_conversion,
                    valueSetter: p => {
                        if (p.data.uom_conversion !== p.newValue) {
                            p.data.uom_conversion = p.newValue
                            return true
                        }
                    }
                })
            }
            if (!this.getFieldSettingsByPath('lines.qty')) {
                columns.push({
                    headerName: 'Qty',
                    fieldName: 'qty',
                    width: 90,
                    type: 'rightAligned',
                    cellClass: 'cell-align-right',
                    valueFormatter: p => p.data.qty,
                    valueGetter: p => p.data.qty,
                    valueSetter: p => {
                        if (p.data.qty !== p.newValue) {
                            p.data.qty = forceToNumber(p.newValue)
                            return true
                        }
                    }
                })
            }
            if (!this.isNexusSandboxTeam && !this.getFieldSettingsByPath('lines.qtyConverted')) {
                columns.push({
                    headerName: 'Converted qty',
                    fieldName: 'qtyConverted',
                    width: 140,
                    type: 'rightAligned',
                    cellClass: 'cell-align-right',
                    editable: false,
                    valueFormatter: p => p.data.qtyConverted,
                    valueGetter: p => p.data.qtyConverted,
                    valueSetter: p => {
                        if (p.data.qtyConverted !== p.newValue) {
                            p.data.qtyConverted = forceToNumber(p.newValue)
                            return true
                        }
                    }
                })
            }
            if (!this.isNexusSandboxTeam && !this.getFieldSettingsByPath('lines.basePrice')) {
                columns.push({
                    headerName: 'Base price',
                    fieldName: 'basePrice',
                    width: 140,
                    type: 'rightAligned',
                    cellClass: 'cell-align-right',
                    cellRendererFramework: AgGridCellRendererPrice,
                    editable: true,
                    valueFormatter: p => p.data.basePrice,
                    valueGetter: p => p.data.basePrice,
                    valueSetter: p => {
                        if (p.data.basePrice !== p.newValue) {
                            p.data.basePrice = forceToNumber(p.newValue)
                            return true
                        }
                    }
                })
            }

            return columns
        },

        getCellSortingComparator(a, b, editor) {
            if (a && !b) {
                return -1
            } else if (b && !a) {
                return 1
            } else if (a && b) {
                const labelA = getFormFieldFormattedLabel({editor, value: a})
                const labelB = getFormFieldFormattedLabel({editor, value: b})
                return labelA === labelB ? 0 : (labelA > labelB) ? 1 : -1
            }
            return 0
        },

        expandRow(idx = 0) {
            this.gridApi.getDisplayedRowAtIndex(idx).setExpanded(true)
        },

        getFieldCellEditorFramework(f) {
            if (getFieldPathMeta(f.path).prop === 'sku') {
                return AgGridCellEditorStockCode
            } else if (f.editor.type === FieldEditorTypes.TEXT && f.editor.multiline) {
                return AgGridCellEditorTextarea
            } else if (f.editor.type === FieldEditorTypes.DATE) {
                return AgGridCellEditorDate
            } else if ([FieldEditorTypes.DD_SINGLE, FieldEditorTypes.DD_MULTIPLE].includes(f.editor.type)) {
                return AgGridCellEditorMultiselect
            }
            return null
        },

        getCellRendererFramework(f) {
            const fieldName = getFieldPathMeta(f.path).prop
            if (fieldName === 'sku') {
                return AgGridCellRendererStockCode
            } else if (['price', 'basePrice'].includes(fieldName)) {
                return AgGridCellRendererPrice
            } else if (fieldName === 'supplierCode') {
                return AgGridCellRendererSupplierCode
            }
            return null
        },

        async setupColumns() {
            if (this.isColumnsSetup) {
                return
            }
            this.isColumnsSetup = true
            const columnDefs = []
            columnDefs.push(this.getLineNumberColumnDef())

            for (const f of this.filteredFields) {
                const {prop, isCustom} = getFieldPathMeta(f.path)
                const col = {}
                col.fieldName = prop
                col.isCustom = isCustom
                col.headerName = evaluateTeamLangPlaceholder(f.label)
                col.cellEditorFramework = this.getFieldCellEditorFramework(f)
                col.cellRendererFramework = this.getCellRendererFramework(f)
                col.cellEditorParams = {settings: f}
                col.cellRendererParams = {settings: f}
                col.tooltipComponentParams = {settings: f}
                col.hide = !(await this.checkIsVisibleField(f))
                col.width = this.getFieldWidth(f)
                col.type = params => this.getCellType(params)
                col.cellClass = params => this.getCellClasses(params)
                col.editable = params => this.checkIsEditableCell(params)
                col.cellStyle = params => this.getCellStyles(params)
                col.valueSetter = params => this.getColValueSetter(params)
                col.valueGetter = params => this.getCellValueGetter(params)
                col.valueFormatter = params => this.getCellValueFormatter(params)

                if (prop === 'sku') {
                    col.cellEditorParams.documentUuid = this.doc.uuid
                    col.cellEditorParams.getResourceState = () => this.docWithNormalizedParts
                }
                if ([FieldEditorTypes.DD_SINGLE, FieldEditorTypes.DD_MULTIPLE].includes(f.editor.type)) {
                    col.cellEditorParams.getResourceState = () => this.docWithNormalizedParts
                    col.comparator = (a, b) => this.getCellSortingComparator(a, b, f.editor)
                    col.filterValueGetter = p => getFormFieldFormattedLabel({
                        editor: f.editor,
                        value: p.colDef.isCustom
                            ? (p.data.custom ? p.data.custom[p.colDef.fieldName] : null)
                            : p.data[p.colDef.fieldName]
                    })
                } else if (f.editor.type === FieldEditorTypes.TEXT && f.editor.multiline) {
                    col.cellEditorParams.cols = 40
                    col.cellEditorParams.rows = 3
                }
                columnDefs.push(col)
                if (col.fieldName === 'sku') {
                    if (this.isDemoTeam && this.matchingSupplier) {
                        columnDefs.push(this.getContractCheckColumnDef())
                    }
                    if (this.withPurchaseOrderColumn && this.purchaseOrder) {
                        columnDefs.push(this.getPoColDef())
                    }
                    if (this.withReceiptsColumn && this.deliveryDocket) {
                        columnDefs.push(this.getReceiptsColDef())
                    }
                    if (this.withLineUomConversion) {
                        columnDefs.push(...this.getUomConversionMissingColumns())
                    }
                }
            }

            this.columnDefs = this.sortColumns(columnDefs)
            this.isColumnsSetup = false
        },

        sortColumns(columnDefs) {
            this.putColumnAfter(columnDefs, 'qtyConverted', 'qty')
            this.putColumnAfter(columnDefs, 'uom_conversion', 'unit_of_measure')
            this.putColumnAfter(columnDefs, 'basePrice', 'price')
            return columnDefs
        },

        putColumnAfter(columnDefs, fieldName, afterFieldName) {
            const colIdx = columnDefs.findIndex(c => c.fieldName === fieldName)
            if (colIdx !== -1) {
                const afterColIdx = columnDefs.findIndex(c => c.fieldName === afterFieldName)
                if (afterColIdx !== -1) {
                    const col = columnDefs.splice(colIdx, 1)[0]
                    columnDefs.splice(afterColIdx, 0, col)
                }
            }
        },

        async checkIsVisibleField(field) {
            if ([FieldEditorTypes.DD_SINGLE, FieldEditorTypes.DD_MULTIPLE].includes(field.editor.type)) {
                const hasOptions = await checkLineSelectFieldHasOptions(field)
                if (!hasOptions) {
                    return false
                }
            }
            return this.checkIsVisibleFieldWithStateRulesApplied(field.path)
        },

        getFieldWidth(field) {
            return field.renderer && isNumber(field.renderer.width) ? field.renderer.width : 90
        },

        getFieldSettingsForCell(params) {
            return get(params, 'colDef.cellEditorParams.settings', {})
        },

        checkIsEditableCell(params) {
            if (this.disabledDocument) {
                return false
            }
            const isAutoPopulatedFields = [
                'basePrice',
                'qtyConverted',
                'supplierCode',
                'onOrder'
            ].includes(params.colDef.fieldName)

            if (isAutoPopulatedFields) {
                return false
            }
            const field = this.getFieldSettingsForCell(params)
            if (!field.editable) {
                return false
            }
            const isVisible = checkIsVisibleFieldInAccordanceWithRules({
                field,
                fields: this.fields,
                idx: params.data.lineIdx,
                doc: this.docWithNormalizedParts
            })
            if (!isVisible) {
                return false
            }
            const isReadonly = checkIsReadonlyFieldInAccordanceWithRules({
                field,
                fields: this.fields,
                idx: params.data.lineIdx,
                doc: this.docWithNormalizedParts
            })
            return !isReadonly
        },

        getCellValueGetter(params) {
            let value
            if (params.colDef.isCustom) {
                value = params.data.custom ? params.data.custom[params.colDef.fieldName] : null
            } else {
                value = params.data[params.colDef.fieldName]
            }
            return value
        },

        getCellValueFormatter(params) {
            let value
            if (params.colDef.isCustom) {
                value = params.data.custom ? params.data.custom[params.colDef.fieldName] : null
            } else {
                value = params.data[params.colDef.fieldName]
            }
            const field = this.getFieldSettingsForCell(params)
            return getFormFieldFormattedLabel({editor: field.editor, value})
        },

        getCellType(params) {
            const field = this.getFieldSettingsForCell(params)
            if (field.renderer.align === FieldRendererAlign.RIGHT || field.editor.type === FieldEditorTypes.NUMERIC) {
                return 'rightAligned'
            }
            return null
        },

        /**
         * NOTE: use valueGetter and valueSetter instead `col.field` to manage column value
         * - there can be nested objects (e.g. for custom fields)
         *       that can't be updated by using ag-grid value management with 'col.field'
         * - there can name conflicts between custom and root line fields
         */
        getColValueSetter(params) {
            if (params.colDef.isCustom) {
                if (!params.data.custom) {
                    params.data.custom = {}
                }
                if (params.data.custom[params.colDef.fieldName] !== params.newValue) {
                    params.data.custom[params.colDef.fieldName] = params.newValue
                    return true
                } else {
                    return false
                }
            } else {
                if (params.data[params.colDef.fieldName] !== params.newValue) {
                    params.data[params.colDef.fieldName] = params.newValue
                    return true
                } else {
                    // NOTE: force value change even if there are no changes
                    // (in order to run check and autofill stock item data)
                    if (params.colDef.fieldName === 'sku') {
                        return true // => run onCellValueChanged logic
                    }
                    return false
                }
            }
        },

        getCellStyles(params) {
            const styles = {'background-color': 'white'}
            if (params.colDef.fieldName === 'sku') {
                styles.padding = '0'
            }
            if (!params.colDef.editable(params)) {
                styles['background-color'] = '#ececec'
                return styles
            }
            const path = params.colDef.cellRendererParams.settings.path
            const metadata = this.getFieldMetadata({path, idx: params.data.lineIdx}) || {}
            if (!metadata) {
                return styles
            }
            const failedValidations = isArray(metadata.validations)
                ? metadata.validations
                    .filter(v => !v.valid)
                    .sort((a, b) => getFieldValidationPriority(b.level) - getFieldValidationPriority(a.level))
                : []
            const showValidationMessages = (this.isTouchedForm || metadata.touched) && failedValidations.length !== 0
            if (showValidationMessages) {
                styles['background-color'] = getFieldValidationLevelMeta(failedValidations[0].level).color
            }
            return styles
        },

        // Unlike cellStyle old classes are not removed - all new classes are applied.
        getCellClasses(params) {
            const field = params.colDef.cellRendererParams.settings
            if (field.renderer.align === FieldRendererAlign.RIGHT || field.editor.type === FieldEditorTypes.NUMERIC) {
                return 'cell-align-right'
            } else if (field.renderer.align === FieldRendererAlign.LEFT) {
                return 'cell-align-left'
            } else if (field.renderer.align === FieldRendererAlign.CENTER) {
                return 'cell-align-center'
            }
        },

        setupLineDefaultValues(idx) {
            if (!this.lines[idx]) {
                return
            }
            this.filteredFields.forEach(field => {
                const defaultValue = this.getLineFieldDefaultValue({field, lineIdx: idx})
                if (!defaultValue) {
                    return
                }
                this.updateField({path: field.path, idx, data: defaultValue})
                this.runSpecialUpdatesOnDefaultValueSet({path: field.path, idx, data: defaultValue})
            })
        },

        setupDefaultValuesForAllLines() {
            const linesBatchUpdateData = this.getFieldDefaultValuesBatchUpdateData()
            this.updateLinesData(linesBatchUpdateData)
            linesBatchUpdateData.forEach(({path, idx, data}) => {
                this.runSpecialUpdatesOnDefaultValueSet({path: `lines.${path}`, idx, data})
            })
        },

        getFieldDefaultValuesBatchUpdateData() {
            const linesBatchUpdateData = []
            this.lines.forEach((line, idx) => {
                if (!line || line.isDeleted) {
                    return
                }
                this.filteredFields.forEach(field => {
                    const defaultValue = this.getLineFieldDefaultValue({field, lineIdx: idx})
                    if (!defaultValue) {
                        return
                    }
                    const {path} = getFieldPathMeta(field.path)
                    const pathWithoutLinesPrefix = path.split('.').slice(1).join('.')
                    linesBatchUpdateData.push({path: pathWithoutLinesPrefix, idx, data: defaultValue})
                })
            })
            return linesBatchUpdateData
        },

        getLineFieldDefaultValue({field, lineIdx}) {
            const hasDefaultValue = typeof field.editor.default !== 'undefined' && field.editor.default !== null
            if (!hasDefaultValue) {
                return null
            }
            const currentValue = this.getFormFieldValue({path: field.path, idx: lineIdx})
            if (!field.editor.forceOverrideWithDefault && currentValue) {
                return null
            }
            const {prop} = getFieldPathMeta(field.path)
            const isSku = prop === 'sku'
            return isSku ? get(field.editor.default, 'sku') : field.editor.default
        },

        runSpecialUpdatesOnDefaultValueSet({path, idx, data}) {
            const {prop} = getFieldPathMeta(path)
            const isSku = prop === 'sku'
            if (isSku) {
                this.onDocLineSkuChange({sku: data, idx})
            } else if (prop === 'taxType') {
                this.recalculateLineTax({idx, emittedByProp: 'taxType'})
            }
        },

        adjustCellValuesBeforeFirstRender() {
            if (this.hasLinesSplitAndTotal) {
                this.recalculateLinesSplit()
            }
        }
    },
    beforeMount() {
        this.frameworkComponents = {
            detailCellRenderer: AgGridCellRendererDetails,
            tooltipComponent: AgGridCellTooltip
        }
        this.detailCellRenderer = 'detailCellRenderer'
        this.getRowNodeId = data => data.lineIdx
        this.sideBar = {
            toolPanels: [
                {
                    id: 'columns',
                    labelDefault: 'Columns',
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel',
                    toolPanelParams: {
                        suppressPivots: true,
                        suppressPivotMode: true,
                        suppressRowGroups: true,
                        suppressValues: true
                    }
                },
                {
                    id: 'filters',
                    labelDefault: 'Filters',
                    labelKey: 'filters',
                    iconKey: 'filter',
                    toolPanel: 'agFiltersToolPanel'
                }
            ],
            position: 'right'
        }

        this.defaultColDef = {
            width: 90,
            sortable: true,
            filter: true,
            editable: true,
            resizable: true,
            tooltipComponent: 'tooltipComponent',
            tooltipValueGetter: p => ({value: p.value}),
            cellClass: params => {
                if (params.colDef.cellRendererFramework) {
                    return 'px-0'
                }
                return 'px-2'
            }
        }

        this.setupDefaultValuesForAllLines()
        this.tryToCalculateConvertedQtyForAllLines()
        this.checkItemOnOrderMetricForAllLines()
    },
    watch: {
        isSavingChanges: {
            handler(isSavingChanges) {
                if (!isSavingChanges) {
                    this.checkItemOnOrderMetricForAllLines()
                }
            }
        },
        firstSubsidiaryId: {
            handler() {
                this.checkItemOnOrderMetricForAllLines()
            }
        },
        lineFieldsMetadata: { // NOTE: this watcher also updated on root fields change
            async handler(newMetadata, prevMetadata) {
                const linesMetadataDiff = newMetadata.filter(obj => !prevMetadata.includes(obj))
                if (this.gridApi && linesMetadataDiff.length !== 0) {
                    this.gridApi.refreshCells({force: true, suppressFlash: true})
                }
            },
            deep: true
        },
        settings: {
            async handler() {
                if (this.isSettingsEditMode && this.gridApi) {
                    await this.setupColumns()
                    this.gridApi.refreshCells({force: true})
                }
            },
            deep: true
        },
        filteredLines: {
            handler() {
                if (this.gridApi) {
                    this.gridApi.setRowData(this.filteredLines)
                }
            },
            deep: true
        },
        purchaseOrder: {
            handler(purchaseOrder) {
                if (!this.withPurchaseOrderColumn) {
                    return
                }
                const colDefs = this.columnDefs || []
                const poColIdx = colDefs.findIndex(c => c.fieldName === 'po')
                if (purchaseOrder) { // add purchase order column
                    if (poColIdx === -1) {
                        const idx = colDefs.findIndex(c => c.fieldName === 'sku')
                        colDefs.splice(idx + 1, 0, this.getPoColDef())
                    }
                } else { // remove purchase order column
                    if (poColIdx !== -1) {
                        colDefs.splice(poColIdx, 1)
                    }
                }
                if (this.gridApi) {
                    this.gridApi.setColumnDefs(colDefs)
                }
            },
            deep: true
        },
        deliveryDocket: {
            handler(deliveryDocket) {
                if (!this.withReceiptsColumn) {
                    return
                }
                const colDefs = this.columnDefs || []
                const receiptColIdx = colDefs.findIndex(c => c.fieldName === 'receipt')
                if (deliveryDocket) { // add purchase order column
                    if (receiptColIdx === -1) {
                        const idx = Math.max(
                            colDefs.findIndex(c => c.fieldName === 'sku'),
                            colDefs.findIndex(c => c.fieldName === 'po')
                        )
                        colDefs.splice(idx + 1, 0, this.getReceiptsColDef())
                    }
                } else { // remove purchase order column
                    if (receiptColIdx !== -1) {
                        colDefs.splice(receiptColIdx, 1)
                    }
                }
                if (this.gridApi) {
                    this.gridApi.setColumnDefs(colDefs)
                }
            },
            deep: true
        },
        visibleFieldsWithStateRulesApplied: {
            handler() {
                this.setupColumns()
            },
            deep: true
        }
    }
}
</script>

<style lang="scss" scoped>
.table-status-bar {
    border: 1px solid #bdc3c7;
    border-radius: 4px;
    border-top: none;
    padding: 8px 12px;
}
</style>

<style lang="scss">
@import "../../../../../../node_modules/ag-grid-enterprise/dist/styles/ag-theme-balham/sass/ag-theme-balham-mixin";

.doc-viewer-lines {
    .ag-theme-balham {
        // Use theme parameters where possible
        // See all possible parameters in: node_modules/ag-grid-enterprise/dist/styles/ag-theme-balham/sass/_ag-theme-balham-default-params.scss
        // or in documentation  https://www.ag-grid.com/documentation/vue/themes-customising/
        @include ag-theme-balham((
            // balham-active-color: $color-purple-primary,
            // grid-size: 5px,
            // font-size: 12px,
        ));

        // or write CSS selectors to make customisations beyond what the parameters support
        //.ag-header-cell-text {
        //    font-style: italic !important;
        //}

        .ag-cell {
            display: flex;
            align-items: center;
        }
    }

    .cell-align-left {
        justify-content: flex-start;
    }

    .cell-align-right {
        justify-content: flex-end;
    }

    .cell-align-center {
        justify-content: center;
    }
}
</style>
